import * as m from 'mithril';
import AppContext from '../context/AppContext';
import { SubFsm } from 'little-fsm';
import { ApiService } from '../ApiService';
import PickUpDropOffSelectionMainPage from '../pages/waypointselection/PickUpDropOffSelectionPage';
import { WayPointSelectionFsmScheme } from '../states/pickup-dropoff-order/WayPointsSelectionFsmScheme';
import LoadingIndicator from '../components/LoadingIndicator';
import { DropOffOrderLineItemDTO, DropOffServiceOptionDTO, PickUpOrderLineItemDTO, PickUpOrderRequestDTO, PickUpServiceOptionDTO } from '../apiclient/ApiDtos';
import PickupInformationFormPage from '../pages/waypointselection/PickupInformationFormPage';
import DropOffInformationFormPage from '../pages/waypointselection/DropOffInformationFormPage';

export type SearchStatus = 'none' | 'pending' | 'success';

export function buildPickUpDropOffSelectionPresenter(
    appContext: AppContext,
    apiService: ApiService,
    subFsm: SubFsm<WayPointSelectionFsmScheme>): { render: () => m.Vnode } {

    let messageProvider = appContext.getMessageProvider();
    let pickupOptions: PickUpServiceOptionDTO[] = [];
    let dropOffOptions: DropOffServiceOptionDTO[] = [];

    let currentEditingPickupLineItem: PickUpOrderLineItemDTO | null = null;
    let currentEditingDropOffLineItem: DropOffOrderLineItemDTO | null = null;

    subFsm.setEnterEffect("initial", (state) => {
        if (state.tripDetails == null) {
            subFsm.processEvent('trip_details_fetching_requested', { tripId: state.tripSelectedContext.selectedTrip.id })
        } else {
            subFsm.processEvent('trip_details_already_reveived', {});
            if (state.tripDetails.pickUpServiceOptions) {
                pickupOptions = state.tripDetails.pickUpServiceOptions;
            }
            if (state.tripDetails.dropOffServiceOptions) {
                dropOffOptions = state.tripDetails.dropOffServiceOptions;
            }
        }
    });

    subFsm.setEnterEffect("fetching_trip_details", (state) => {
        let tripResult = state.tripSelectedContext.selectedTrip;
        apiService.getTrip(tripResult.id).then(result => {
            if (result.right) {
                let tripDetails = result.right;
                console.debug("Got Trip Details: ", tripDetails);
                subFsm.processEvent('trip_details_received', { tripDetails: tripDetails })
                if (tripDetails.pickUpServiceOptions) {
                    pickupOptions = tripDetails.pickUpServiceOptions;
                }
                if (tripDetails.dropOffServiceOptions) {
                    dropOffOptions = tripDetails.dropOffServiceOptions;
                }
            }

        });
    });

    subFsm.setEnterEffect('ordering_waypoints', c => {
        let newPickUpOrder: PickUpOrderRequestDTO = {
            lineItems: c.addedPickupLineItems
        };
        let newDropOffOrder: PickUpOrderRequestDTO = {
            lineItems: c.addedDropOffLineItems
        };

        apiService.updateTripCustimazion(c.tripCustomization.id, newPickUpOrder, newDropOffOrder).then(result => {
            if (result.right) {
                subFsm.processEvent('waypoint_order_succeed', { tripCustomization: result.right })
            } else {
                subFsm.processEvent('waypoint_order_failed', { errorMessage: { en: "Failed", my: "Failed" } })
            }
        })

    });

    return {
        render() {
            let context = subFsm.getCurrentContext();
            let substate = subFsm.getCurrentSubState();

            let totalSeatsForTrip = context.tripSelectedContext.tripSearchContext.searchCriteria.numberOfSeats;

            let totalSelectedSeatsForPickUp = context.addedPickupLineItems.map(it => it.numberOfSeats).reduce((a, c) => a + c, 0);
            let totalSelectedSeatsForDropOff = context.addedDropOffLineItems.map(it => it.numberOfSeats).reduce((a, c) => a + c, 0);

            let totalRemainingSeatsForPickUp = totalSeatsForTrip - totalSelectedSeatsForPickUp;
            let totalRemainingSeatsForDropOff = totalSeatsForTrip - totalSelectedSeatsForDropOff;

            return substate == 'initial' || context.tripDetails == null ?
                m(LoadingIndicator) :

                (substate == 'entering_pickup_details') ?
                    m(PickupInformationFormPage, {
                        appContext: appContext,
                        currentEditingPickupLineItem: currentEditingPickupLineItem,
                        maxSeatCount: (currentEditingPickupLineItem != null) ?
                            totalRemainingSeatsForPickUp + currentEditingPickupLineItem.numberOfSeats :
                            totalRemainingSeatsForPickUp,
                        totalNumberOfTravellers: totalSeatsForTrip,
                        messageProvider: appContext.getMessageProvider(),
                        title: messageProvider.get("way_point_selection.choose_pickup_title"),
                        pickupOptions: pickupOptions,
                        onPickupOrderLineItemAdded: (lineItem: PickUpOrderLineItemDTO) => {
                            let addedPickupLineItems = [...context.addedPickupLineItems];
                            addedPickupLineItems.push(lineItem)
                            subFsm.processEvent('pickup_line_items_changed', { lineItems: addedPickupLineItems })
                            currentEditingPickupLineItem = null;
                        },
                        onPickupOrderLineItemUpdated: (updatedLineItem: PickUpOrderLineItemDTO, existingItem: PickUpOrderLineItemDTO) => {
                            console.log('onPickupOrderLineItemUpdated: ', updatedLineItem);

                            let addedPickupLineItems = context.addedPickupLineItems.map(it => it == currentEditingPickupLineItem ? updatedLineItem : it);
                            subFsm.processEvent('pickup_line_items_changed', { lineItems: addedPickupLineItems })
                            currentEditingPickupLineItem = null;
                        },
                        onBack: () => {
                            subFsm.processEvent('back', {});
                        }
                    }) :

                    (substate == 'entering_dropoff_details') ?
                        m(DropOffInformationFormPage, {
                            appContext: appContext,
                            currentEditingDropOffLineItem: currentEditingDropOffLineItem,
                            maxSeatCount: (currentEditingDropOffLineItem != null) ?
                                totalRemainingSeatsForDropOff + currentEditingDropOffLineItem.numberOfSeats :
                                totalRemainingSeatsForDropOff,
                            totalNumberOfTravellers: totalSeatsForTrip,
                            messageProvider: appContext.getMessageProvider(),
                            title: messageProvider.get("way_point_selection.choose_dropoff_title"),
                            dropOffOptions: dropOffOptions,
                            onDropOffOrderLineItemAdded: (lineItem: DropOffOrderLineItemDTO) => {
                                let updatedDropOffLineItems = [...context.addedDropOffLineItems];
                                updatedDropOffLineItems.push(lineItem)
                                subFsm.processEvent('dropoff_line_items_changed', { lineItems: updatedDropOffLineItems })
                                currentEditingDropOffLineItem = null;
                            },
                            onDropOffOrderLineItemUpdated: (updatedLineItem: DropOffOrderLineItemDTO, existingItem: DropOffOrderLineItemDTO) => {
                                console.log('onDropOffOrderLineItemUpdated: ', updatedLineItem);

                                let items = context.addedDropOffLineItems.map(it => it == currentEditingDropOffLineItem ? updatedLineItem : it);
                                subFsm.processEvent('dropoff_line_items_changed', { lineItems: items })
                                currentEditingDropOffLineItem = null;
                            },

                            onBack: () => {
                                subFsm.processEvent('back', {});
                            }
                        }) :


                        m(PickUpDropOffSelectionMainPage, {
                            appContext: appContext,
                            searchCriteria: context.tripSelectedContext.tripSearchContext.searchCriteria,
                            tripDetails: context.tripDetails!,
                            seatSelections: context.selectedSeats,
                            messageProvider: appContext.getMessageProvider(),
                            selectedPickupOptions: context.addedPickupLineItems,
                            selectedDropOffOptions: context.addedDropOffLineItems,
                            errorMessage: context.errorMessage ? context.errorMessage : null,
                            isBacktracking: substate == 'back_navigation_confirming',
                            tripCustomization: context.tripCustomization,
                            onBacktrack: () => {
                                subFsm.processEvent('back', {});
                            },
                            onBacktrackConfirmed: () => {
                                subFsm.processEvent('back_confirmed', {});
                            },
                            onBacktrackCancelled: () => {
                                subFsm.processEvent('back_cancelled', {});
                            },

                            onPickUpAddRequested: () => {
                                currentEditingPickupLineItem = null;
                                subFsm.processEvent('pickup_line_item_entry_requested', {})
                            },
                            onPickUpEditRequested: (index: number) => {
                                currentEditingPickupLineItem = context.addedPickupLineItems[index]
                                subFsm.processEvent('pickup_line_item_entry_requested', {})
                            },
                            onPickUpRemoveRequested: (item: PickUpOrderLineItemDTO) => {
                                let items = context.addedPickupLineItems.filter(it => it != item);
                                subFsm.processEvent('pickup_line_items_changed', { lineItems: items })
                            },

                            onDropOffAddRequested: () => {
                                currentEditingDropOffLineItem = null;
                                subFsm.processEvent('dropoff_line_item_entry_requested', {})
                            },
                            onDropOffEditRequested: (index: number) => {
                                currentEditingDropOffLineItem = context.addedDropOffLineItems[index];
                                subFsm.processEvent('dropoff_line_item_entry_requested', {})
                            },
                            onDropOffRemoveRequested: (item: DropOffOrderLineItemDTO) => {
                                console.log("Remove drop off item:", item);
                                let items = context.addedDropOffLineItems.filter(it => it != item);
                                subFsm.processEvent('dropoff_line_items_changed', { lineItems: items })
                            },

                            onSubmitPickUpDropOff: () => {
                                currentEditingPickupLineItem = null;
                                currentEditingDropOffLineItem = null;

                                if (context.tripCustomization.pickUpOrder && context.tripCustomization.dropOffOrder
                                    && context.tripCustomization.pickUpOrder.lineItems.length > 0 && context.tripCustomization.dropOffOrder.lineItems.length > 0
                                ) {
                                    const pickupUnchanged = isSameLineItems(
                                        context.tripCustomization.pickUpOrder.lineItems,
                                        context.addedPickupLineItems
                                    );

                                    const dropOffUnchanged = isSameLineItems(
                                        context.tripCustomization.dropOffOrder.lineItems,
                                        context.addedDropOffLineItems
                                    );

                                    if (pickupUnchanged && dropOffUnchanged) {
                                        subFsm.processEvent('completed', {});
                                    } else {
                                        subFsm.processEvent('waypoint_order_requested', {});
                                    }
                                } else {
                                    subFsm.processEvent('waypoint_order_requested', {})
                                }
                            }
                        })
        }
    }
}
function isSameLineItems<
    T extends { option: { id: string }; numberOfSeats: number; address: string }
>(a: T[], b: T[]): boolean {
    if (a.length !== b.length) return false;

    const sortFn = (x: T, y: T) => x.option.id.localeCompare(y.option.id);
    const sortedA = [...a].sort(sortFn);
    const sortedB = [...b].sort(sortFn);

    return sortedA.every((item, index) =>
        item.option.id === sortedB[index].option.id &&
        item.numberOfSeats === sortedB[index].numberOfSeats &&
        item.address.trim() === sortedB[index].address.trim()
    );
}